import React, { useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import style from "./EditAmountModal.module.scss";
import { Expeditor } from "../../../../../services";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomTextarea from "../../../../custom/custom-textarea/CustomTextarea";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import { CURRENCIES } from "../../../../../mock";
import { useTranslation } from "react-i18next";
import { useTenderActionError } from "../../../../../hooks/useTenderActionError";

const formatPrice = (value, currency = "KZT") => {
  if (value === null || value === undefined || value === "") return "—";
  const formatted = Number(value).toLocaleString("ru-RU").replace(/,/g, " ");
  const resolvedCurrency = currency || "KZT";
  const symbol = CURRENCIES[resolvedCurrency]?.symbol || resolvedCurrency;
  return `${formatted} ${symbol}`;
};

const EditAmountModal = ({ isOpen, onClose, orderId, currentPrice, currency = "KZT", onSuccess, onRefreshOrder }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError({ id: orderId }, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [comment, setComment] = useState("");
  const [errorText, setErrorText] = useState("");
  const [commentErrorText, setCommentErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setPrice("");
    setComment("");
    setErrorText("");
    setCommentErrorText("");
    onClose();
  };

  const handleSave = async () => {
    const rounded = Math.round(Number(price));
    const trimmedComment = comment.trim();
    if (!rounded || rounded <= 0) {
      setErrorText(t("orderInfoExpeditor.editAmount.correctAmountRequired"));
      return;
    }
    if (rounded > 2000000000) {
      setErrorText(t("orderInfoExpeditor.editAmount.amountTooLarge"));
      return;
    }

    if (!trimmedComment) {
      setCommentErrorText(t("orderInfoExpeditor.editAmount.reasonRequired"));
      toast.error(t("orderInfoExpeditor.editAmount.reasonRequired"));
      return;
    }

    setIsLoading(true);
    try {
      await Expeditor.updateOrderBidPrice(orderId, {
        price: rounded,
        comment: trimmedComment,
        currency: currency,
      });
      toast.success(t("orderInfoExpeditor.editAmount.success"));
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      await requestErrorDisplay(error, t, "orderInfoExpeditor.editAmount.error");
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("orderInfoExpeditor.editAmount.title")}</span>
          <button className={style.close_btn} onClick={handleClose} aria-label={t("orderInfoExpeditor.editAmount.close")}>
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>

        <div className={style.body}>
          <CustomInput
            isDisabled
            isBgGray
            inputType="text"
            labelText={t("orderInfoExpeditor.editAmount.currentBid")}
            value={formatPrice(currentPrice, currency)}
          />

          <CustomInputCurrency
            isBgGray
            lockCurrency
            inputType="number"
            labelText={t("orderInfoExpeditor.editAmount.newBidRequired")}
            placeholder={t("orderInfoExpeditor.editAmount.newBidPlaceholder")}
            currency={currency}
            value={price}
            errorText={errorText}
            onChangeValue={(val) => {
              setPrice(val);
              setErrorText("");
            }}
          />

          <CustomTextarea
            isBgGray
            rows={2}
            labelText={t("orderInfoExpeditor.editAmount.reasonRequiredLabel")}
            placeholder={t("orderInfoExpeditor.editAmount.reasonPlaceholder")}
            value={comment}
            errorText={commentErrorText}
            onChangeValue={(val) => {
              setComment(val);
              setCommentErrorText("");
            }}
          />
        </div>

        <div className={style.footer}>
          <CustomButton
            text={t("common.cancel")}
            isNavy
            isWhite
            onClick={handleClose}
            disabled={isLoading}
          />
          <CustomButton
            text={t("common.save")}
            isNavy
            onClick={handleSave}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditAmountModal;
